import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { Menu, Transition } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  FlagIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import Modal from "../../layouts/Modal";
import useEbayMessageChain from "../../data/useEbayMessagesChain";
import usePartByEbayItem from "../../data/usePartByEbayItem";
import usePartInvoice from "../../data/usePartInvoice";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import stringToColour from "../../utils/stringToColour";
import {
  IEbayMessage,
  IEbayMessageTemplate,
} from "../../models/EbayMessage";
import Avatar from "../user/Avatar";
import LoadingWheel from "../shared/LoadingWheel";
import Tooltip from "../shared/Tooltip";
import StockHeader from "../stock/viewStock/StockHeader";
import {
  SAMPLE_EBAY_MESSAGE_TEMPLATES,
  fillTemplate,
} from "./ebayMessageTemplates";

interface EbayMessageChainProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  originMessage: IEbayMessage | undefined;
}

const STAFF_SENDER_ID = "-99";

const isStaffMessage = (m: IEbayMessage): boolean =>
  m.sendingUserID === STAFF_SENDER_ID;

const initialsFor = (name: string | undefined | null): string => {
  if (!name) return "?";
  return (
    name
      .split(" ")
      .filter((word) => RegExp(/^\p{L}/, "u").test(word))
      .map((word, i) => (i < 2 ? word.substring(0, 1).toUpperCase() : ""))
      .join("") || name.substring(0, 2).toUpperCase()
  );
};

const isFlaggedMsg = (message: IEbayMessage | undefined): boolean => {
  const flagged = message?.flagged;
  if (!flagged) return false;
  return (
    flagged === true ||
    flagged === 1 ||
    String(flagged).toLowerCase() === "true" ||
    String(flagged) === "1"
  );
};

export default function EbayMessageChain({
  originMessage,
  open,
  setOpen,
}: EbayMessageChainProps) {
  return (
    <Modal open={open} setOpen={setOpen} width="max-w-7xl">
      {open ? (
        <ChainBody
          originMessage={originMessage}
          onClose={() => setOpen(false)}
        />
      ) : (
        <div aria-hidden />
      )}
    </Modal>
  );
}

interface ChainBodyProps {
  originMessage: IEbayMessage | undefined;
  onClose: () => void;
}

/**
 * All the heavy lifting (query subscription, effects, DOM refs) is
 * isolated to this inner component so it only mounts while the modal
 * is open. That keeps the parent conversation list cheap to re-render
 * and avoids subscribing effects/queries when we'd never use them.
 */
function ChainBody({ originMessage, onClose }: ChainBodyProps) {
  const { messages, sendReply } = useEbayMessageChain(originMessage);
  const { part: linkedPart, isLoading: isLoadingPart } = usePartByEbayItem(
    originMessage?.itemID
  );
  // Pull the linked part's invoice (if any) so sold parts render the
  // same invoice-aware header section they do on the stock detail page.
  const { invoice: linkedInvoice } = usePartInvoice(linkedPart?.id);
  const [body, setBody] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const feedEndRef = useRef<HTMLDivElement | null>(null);

  const buyerName = originMessage?.sendingUserID ?? "Customer";
  const buyerColour = useMemo(() => stringToColour(buyerName), [buyerName]);

  const sortedMessages = useMemo(() => {
    const list = messages.data?.data ?? [];
    return [...list].sort((a, b) => {
      const aIso = DateTime.fromISO(a.receiveDate);
      const bIso = DateTime.fromISO(b.receiveDate);
      const aVal = aIso.isValid ? aIso.toMillis() : 0;
      const bVal = bIso.isValid ? bIso.toMillis() : 0;
      return aVal - bVal;
    });
  }, [messages.data]);

  const templatesByCategory = useMemo(() => {
    const groups = new Map<string, IEbayMessageTemplate[]>();
    for (const t of SAMPLE_EBAY_MESSAGE_TEMPLATES) {
      const key = t.category ?? "General";
      const arr = groups.get(key) ?? [];
      arr.push(t);
      groups.set(key, arr);
    }
    return Array.from(groups.entries());
  }, []);

  useEffect(() => {
    feedEndRef.current?.scrollIntoView({ block: "end" });
  }, [sortedMessages.length]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // Generous min of ~7 lines and cap at ~18 lines so the composer feels
    // like a proper writing surface, not a one-line reply box.
    const MIN_HEIGHT = 168;
    const MAX_HEIGHT = 460;
    el.style.height = `${Math.min(
      Math.max(el.scrollHeight, MIN_HEIGHT),
      MAX_HEIGHT
    )}px`;
  }, [body]);

  const sendMessage = () => {
    const trimmed = body.trim();
    if (!trimmed || sendReply.isLoading) return;
    sendReply.mutate(
      { body: trimmed },
      {
        onSuccess: () => setBody(""),
      }
    );
  };

  const handleComposerKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      sendMessage();
    }
  };

  const applyTemplate = (template: IEbayMessageTemplate) => {
    const filled = fillTemplate(template, originMessage);
    setBody((prev) => {
      if (!prev.trim()) return filled;
      return `${prev.trimEnd()}\n\n${filled}`;
    });
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const isLoading = messages.isLoading;
  const flagged = isFlaggedMsg(originMessage);

  // Always use the itemID from the message so the link lands on the exact
  // listing the buyer is writing about, even if we couldn't resolve it to a
  // stock part. Falls back to the part's ebayItemNumber when the message
  // itself has no id.
  const ebayItemId = originMessage?.itemID ?? linkedPart?.ebayItemNumber ?? null;
  const ebayListingUrl = ebayItemId
    ? `https://www.ebay.co.uk/itm/${ebayItemId}`
    : null;
  const stockPageUrl = linkedPart?.id ? `/stock/${linkedPart.id}` : null;

  return (
    <div className="-m-6 flex h-[92vh] max-h-[95vh] flex-col overflow-hidden rounded-lg bg-white">
      <header className="flex items-center gap-3 border-b border-slate-200 bg-gradient-to-r from-primary-50/60 to-white px-5 py-3">
        <span
          className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full text-sm font-semibold text-white shadow-inner"
          style={{ background: buyerColour }}
          aria-hidden
        >
          {initialsFor(buyerName)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-semibold text-slate-900">
              {buyerName}
            </p>
            {flagged && (
              <Tooltip content="Flagged for assistance">
                <FlagIcon
                  className="h-4 w-4 text-rose-500"
                  aria-label="Flagged"
                />
              </Tooltip>
            )}
          </div>
          <p className="truncate text-xs text-slate-500">
            {originMessage?.itemTitle ?? "eBay conversation"}
            {ebayItemId && (
              <span className="ml-1 text-slate-400">
                · Item{" "}
                <span className="font-mono text-slate-500">{ebayItemId}</span>
              </span>
            )}
          </p>
        </div>
        {stockPageUrl && (
          <a
            href={stockPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            title="Open the full stock page for this part"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
            View stock page
          </a>
        )}
        {ebayListingUrl && (
          <a
            href={ebayListingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
            title={`Open listing ${ebayItemId} on eBay`}
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
            View on eBay
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden />
        </button>
      </header>

      {/* Stock header — same top-of-page summary as the parts stock view,
          so the replier has the full part context (image, grade, price,
          time in stock, quick actions) without leaving the thread. */}
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
        {isLoadingPart ? (
          <div className="flex h-32 items-center justify-center rounded-lg bg-white shadow">
            <LoadingWheel />
          </div>
        ) : linkedPart ? (
          <StockHeader
            part={linkedPart}
            invoice={linkedInvoice?.data?.data ?? null}
          />
        ) : (
          <EbayListingFallbackHeader originMessage={originMessage} />
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-slate-50 px-4 py-4 sm:px-6">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center py-10">
              <LoadingWheel />
            </div>
          ) : sortedMessages.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-3">
              {sortedMessages.map((message, i) => (
                <ChatBubble
                  key={message.messageID ?? `${message.receiveDate}-${i}`}
                  message={message}
                  buyerName={buyerName}
                  buyerColour={buyerColour}
                />
              ))}
            </ul>
          )}
          <div ref={feedEndRef} />
        </div>

        <footer className="border-t border-slate-200 bg-white px-4 py-4 sm:px-6">
          <div className="flex items-stretch gap-3">
            <div className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
              <textarea
                ref={textareaRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={handleComposerKey}
                placeholder="Write a reply — Cmd / Ctrl + Enter to send"
                rows={7}
                className="block w-full resize-none rounded-xl border-0 bg-transparent px-4 py-3 text-base leading-relaxed text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-0"
              />
              <div className="flex items-center justify-between border-t border-slate-100 px-2 py-1">
                <TemplatePicker
                  templatesByCategory={templatesByCategory}
                  onPick={applyTemplate}
                />
                <p className="pr-1 text-[11px] text-slate-400">
                  {body.trim().length.toLocaleString()} chars
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={sendMessage}
              disabled={!body.trim() || sendReply.isLoading}
              className={classNames(
                "inline-flex min-w-[104px] flex-none flex-col items-center justify-center gap-1 self-stretch rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition-colors",
                !body.trim() || sendReply.isLoading
                  ? "bg-slate-300 cursor-not-allowed"
                  : "bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
              )}
              aria-label="Send reply"
            >
              <PaperAirplaneIcon className="h-5 w-5" aria-hidden />
              <span>{sendReply.isLoading ? "Sending…" : "Send"}</span>
              <span className="text-[10px] font-normal text-white/75">
                ⌘ + ↵
              </span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

interface EbayListingFallbackHeaderProps {
  originMessage: IEbayMessage | undefined;
}

/**
 * Lightweight "part unavailable" stand-in shown at the top of the
 * message modal when the pending `GET /part/byebay/:itemNumber`
 * endpoint returns nothing. Mirrors the visual shape of StockHeader
 * so the modal doesn't jump around vertically once the real part
 * loads, and always exposes the eBay link so the replier can still
 * investigate the listing.
 */
function EbayListingFallbackHeader({
  originMessage,
}: EbayListingFallbackHeaderProps) {
  const ebayUrl = originMessage?.itemID
    ? `https://www.ebay.co.uk/itm/${originMessage.itemID}`
    : null;

  return (
    <div className="rounded-lg bg-white p-4 shadow sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            eBay listing
          </p>
          <h2 className="mt-1 text-lg font-semibold leading-snug text-slate-900 line-clamp-2">
            {originMessage?.itemTitle ?? "No listing title"}
          </h2>
          {originMessage?.itemID && (
            <p className="mt-1 text-xs text-slate-500">
              Item{" "}
              <span className="font-mono text-slate-700">
                {originMessage.itemID}
              </span>
            </p>
          )}
          <p className="mt-2 max-w-xl text-xs text-slate-500">
            Stock details couldn&apos;t be loaded for this listing.
            {originMessage?.itemID &&
              " Open the listing on eBay to see the live details."}
          </p>
        </div>

        {ebayUrl && (
          <a
            href={ebayUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 self-start rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
            View on eBay
          </a>
        )}
      </div>
    </div>
  );
}

interface ChatBubbleProps {
  message: IEbayMessage;
  buyerName: string;
  buyerColour: string;
}

function ChatBubble({ message, buyerName, buyerColour }: ChatBubbleProps) {
  const staff = isStaffMessage(message);
  const timestamp = formatDateTime(
    message.receiveDate,
    DateTime.DATETIME_MED,
    ""
  );

  return (
    <li
      className={classNames(
        "flex items-end gap-2",
        staff ? "justify-end" : "justify-start"
      )}
    >
      {!staff && (
        <span
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white shadow-inner"
          style={{ background: buyerColour }}
          aria-hidden
          title={buyerName}
        >
          {initialsFor(buyerName)}
        </span>
      )}

      <div
        className={classNames(
          "flex max-w-[78%] flex-col",
          staff ? "items-end" : "items-start"
        )}
      >
        <div
          className={classNames(
            "whitespace-pre-line rounded-2xl px-3.5 py-2 text-sm shadow-sm ring-1 ring-inset",
            staff
              ? "bg-primary-600 text-white ring-primary-700 rounded-br-sm"
              : "bg-white text-slate-800 ring-slate-200 rounded-bl-sm"
          )}
        >
          {message.body || message.text || (
            <span className="italic opacity-70">(no body)</span>
          )}
        </div>
        {timestamp && (
          <span className="mt-1 px-1 text-[11px] text-slate-400">
            {staff ? "You · " : ""}
            {timestamp}
          </span>
        )}
      </div>

      {staff && (
        <Avatar
          classes="w-7 h-7 rounded-full ring-2 ring-white shadow-sm"
          textClasses="text-[11px]"
        />
      )}
    </li>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-10 text-center text-slate-400">
      <ChatBubbleLeftRightIcon className="h-10 w-10" aria-hidden />
      <p className="mt-2 text-sm">No messages in this thread yet.</p>
    </div>
  );
}

interface TemplatePickerProps {
  templatesByCategory: Array<[string, IEbayMessageTemplate[]]>;
  onPick: (template: IEbayMessageTemplate) => void;
}

function TemplatePicker({ templatesByCategory, onPick }: TemplatePickerProps) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-300">
        <DocumentTextIcon className="h-4 w-4" aria-hidden />
        Templates
        <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Menu.Items className="absolute bottom-full left-0 z-30 mb-1 w-80 origin-bottom-left overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-slate-200 focus:outline-none">
          <div className="max-h-72 overflow-y-auto py-1">
            {templatesByCategory.length === 0 && (
              <p className="px-3 py-4 text-xs text-slate-500">
                No templates available
              </p>
            )}
            {templatesByCategory.map(([category, templates]) => (
              <div key={category} className="py-1">
                <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  {category}
                </p>
                {templates.map((template, idx) => (
                  <Menu.Item
                    key={template.id ?? `${category}-${idx}`}
                  >
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => onPick(template)}
                        className={classNames(
                          "flex w-full flex-col px-3 py-1.5 text-left text-xs transition-colors",
                          active ? "bg-primary-50" : "bg-white"
                        )}
                      >
                        <span className="font-medium text-slate-800">
                          {template.name}
                        </span>
                        <span className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">
                          {template.body}
                        </span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] text-slate-500">
            Placeholders: <code>{"{{buyer}}"}</code>{" "}
            <code>{"{{listing}}"}</code> <code>{"{{item}}"}</code>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
